import { Button, Input } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { DataTable, StatusChip, type IDataColumn } from '@/design';
import { Struck, leavingRowProps, useLeaving } from '../chrome/leaving';
import { DemoSelect, MockPanel } from '../chrome/marks';
import { MockScreen, ViewOnlyNote, useScreenGrant } from '../chrome/mock-screen';
import { firmName, type IDemoPerson } from '../fixtures';
import { day, daysUntil, millions } from '../format';
import { useActingAs, useDemo } from '../state/demo-state';
import type { IThresholds } from '../state/initial-review';
import {
  EXCLUSION_REASONS,
  SURVEY_EXCLUSION_REASONS,
  SURVEY_TYPE_LABEL,
  type Eligibility,
  type IExclusionReason,
  type SurveyType
} from '../vocabulary';

/**
 * The initial review (step 15, RNK-04): every record nobody has reviewed yet this cycle, with the
 * survey owner's "in consideration" flag. A screen of its own, not a view (ruling R5): its
 * thresholds are the survey owner's, and its decision needs a reason. It stood beside the worklists
 * mock, deleted when SR-230 made Lists live (the Lists design's Q3), and kept its address; its
 * survey and thresholds are a state of its own (`state/initial-review.ts`), its decisions the
 * records'.
 *
 * **Two capabilities.** Its thresholds are the survey owner's (§5, §6.1 — "in consideration" has no
 * requirement ID), which is the screen's `acts`; its decisions are list work, which anyone who opens
 * the screen does (`opens`). A decided record leaves the list (`useLeaving`).
 */

const MISSING_INITIAL =
  'Nothing stores eligibility or a threshold yet: this needs the survey owner’s in-consideration formula on the cycle, and an eligibility status with its reasons on each record.';

/** The 2027 survey stays open until its review date; "in consideration" is flagged until then. */
const SURVEY_CLOSES = '2026-10-31';

export function InitialReviewPage() {
  return (
    <MockScreen
      screen="initial-review"
      title="Initial review"
      lede="Every record nobody has reviewed yet this cycle, with the survey owner’s “in consideration” flag. Decide each one; a decided record leaves."
      missing={MISSING_INITIAL}
    >
      <Thresholds />
      <InitialReviewList />
    </MockScreen>
  );
}

const SURVEYS: readonly SurveyType[] = ['FA', 'TEAMS', 'FSP', 'RIA_FIRM'];

/** The survey the list shows, chosen on its own picker — the header's scope that once set it
 *  too went with SR-439. */
function useReviewSurvey(): [SurveyType, (survey: SurveyType) => void] {
  const { state, dispatch } = useDemo();
  return [
    state.initialReview.survey,
    (survey) => dispatch({ type: 'initial-review/survey', survey })
  ];
}

function SurveyPicker({
  survey,
  onChange
}: {
  survey: SurveyType;
  onChange: (survey: SurveyType) => void;
}) {
  return (
    <label className="grid gap-1 text-[0.8125rem]">
      Survey
      <DemoSelect
        value={survey}
        onChange={(event) => onChange(event.target.value as SurveyType)}
        className="h-8 min-w-36"
      >
        {SURVEYS.map((code) => (
          <option key={code} value={code}>
            {SURVEY_TYPE_LABEL[code]} — 2027 cycle
          </option>
        ))}
      </DemoSelect>
    </label>
  );
}

interface IListRow {
  person: IDemoPerson;
  /** Why the row is leaving, while it is a ghost. */
  leaving: Nullable<string>;
}

function AdvisorCell({ row }: { row: IListRow }) {
  const { person } = row;
  const name = (
    <span className="grid">
      <span className="font-medium">{person.name}</span>
      <span className="text-xs text-muted-foreground">
        {person.idType} {person.number}
      </span>
    </span>
  );
  if (!row.leaving) return name;
  return (
    <span className="grid gap-1">
      <Struck>{name}</Struck>
      <span className="text-xs font-medium text-[var(--color-royal-blue)]">Leaves the list: {row.leaving}</span>
    </span>
  );
}

function reasonsFor(survey: SurveyType): readonly IExclusionReason[] {
  return [...EXCLUSION_REASONS, ...SURVEY_EXCLUSION_REASONS].filter(
    (reason) => !reason.surveys || reason.surveys.includes(survey)
  );
}

type ThresholdRule = Pick<IThresholds, 'aum' | 'production' | 'op'>;

function crosses(entry: IDemoPerson, thresholds: ThresholdRule): boolean {
  const aum = entry.aum >= thresholds.aum;
  const production = entry.production >= thresholds.production;
  return thresholds.op === 'AND' ? aum && production : aum || production;
}

function formula(thresholds: ThresholdRule): string {
  const aum = millions(thresholds.aum);
  return `AUM at least ${aum} ${thresholds.op} production at least ${millions(thresholds.production)}`;
}

function Thresholds() {
  const { state } = useDemo();
  const { thresholds } = state.initialReview;
  // Keyed on what was saved, so "Reset demo" or another screen's save redraws the fields.
  return (
    <ThresholdsForm
      key={`${thresholds.aum}|${thresholds.production}|${thresholds.op}|${thresholds.setBy}|${thresholds.setAt}`}
    />
  );
}

function ThresholdsForm() {
  const { state, dispatch } = useDemo();
  const { acts } = useScreenGrant();
  const acting = useActingAs();
  const { thresholds } = state.initialReview;
  const [aum, setAum] = React.useState(String(thresholds.aum));
  const [production, setProduction] = React.useState(String(thresholds.production));
  const [op, setOp] = React.useState<'AND' | 'OR'>(thresholds.op);

  const valid = aum !== '' && production !== '' && Number(aum) >= 0 && Number(production) >= 0;
  const dirty =
    Number(aum) !== thresholds.aum || Number(production) !== thresholds.production || op !== thresholds.op;

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || !acts) return;
    dispatch({ type: 'thresholds/set', aum: Number(aum), production: Number(production), op, by: acting.name });
  };

  return (
    <MockPanel
      title="In-consideration thresholds"
      source="no-endpoint"
      description={`Set by the survey owner before the survey opens. Now: ${formula(thresholds)}.`}
    >
      <form onSubmit={save} className="grid gap-3 text-[0.8125rem]">
        <fieldset disabled={!acts} className="flex flex-wrap items-end gap-3">
          <legend className="sr-only">Thresholds</legend>
          <label className="grid gap-1">
            AUM at least ($M)
            <Input
              type="number"
              min={0}
              value={aum}
              onChange={(event) => setAum(event.target.value)}
              className="h-8 w-32 tabular-nums"
            />
          </label>
          <label className="grid gap-1">
            Joined by
            <DemoSelect value={op} onChange={(event) => setOp(event.target.value as 'AND' | 'OR')} className="h-8">
              <option value="OR">OR — either one</option>
              <option value="AND">AND — both</option>
            </DemoSelect>
          </label>
          <label className="grid gap-1">
            Production at least ($M)
            <Input
              type="number"
              min={0}
              step="0.1"
              value={production}
              onChange={(event) => setProduction(event.target.value)}
              className="h-8 w-32 tabular-nums"
            />
          </label>
          <Button type="submit" className="h-8" disabled={!valid || !dirty}>
            Save the thresholds
          </Button>
        </fieldset>
        <p className="text-muted-foreground">
          Set by {thresholds.setBy} on {day(thresholds.setAt)}. The formula has no requirement ID: it is two
          notes in PRS §5 and §6.1.
        </p>
        <ViewOnlyNote what="the thresholds" />
      </form>
    </MockPanel>
  );
}

type Decision = Exclude<Eligibility, 'Not yet reviewed'>;

const DECISIONS: readonly Decision[] = ['Eligible', 'Under review', 'Not eligible'];

interface IDraft {
  decision: '' | Decision;
  reason: string;
}

/** Whether a draft can be recorded: a decision, and a reason where the decision is Not eligible. */
function isReady(draft: IDraft): draft is IDraft & { decision: Decision } {
  return draft.decision !== '' && (draft.decision !== 'Not eligible' || draft.reason !== '');
}

function InitialReviewList() {
  const { state, dispatch } = useDemo();
  // Deciding a record is list work, whoever may set the thresholds (see the module's note).
  const { opens: acts } = useScreenGrant();
  const acting = useActingAs();
  const [survey, setSurvey] = useReviewSurvey();
  const [drafts, setDrafts] = React.useState<Record<string, IDraft>>({});
  const { thresholds } = state.initialReview;
  const open = daysUntil(SURVEY_CLOSES) >= 0;

  const inSurvey = state.act2.people.filter((entry) => entry.survey === survey);
  const members = inSurvey.filter((entry) => entry.eligibility === 'Not yet reviewed').map((entry) => entry.id);
  const { watch, leaving, said } = useLeaving(members, (id) => {
    const entry = state.act2.people.find((candidate) => candidate.id === id);
    const reason =
      entry?.quality && 'reason' in entry.quality && entry.eligibility === 'Not eligible'
        ? ` (${entry.quality.reason})`
        : '';
    return {
      name: entry?.name ?? id,
      reason: `marked ${entry?.eligibility ?? 'reviewed'}${reason} — no longer Not yet reviewed`
    };
  });
  const rows: IListRow[] = inSurvey
    .filter((entry) => members.includes(entry.id) || leaving.has(entry.id))
    .map((entry) => ({ person: entry, leaving: leaving.get(entry.id) ?? null }));
  const reasons = reasonsFor(survey);
  const flagged = inSurvey.filter((entry) => members.includes(entry.id) && crosses(entry, thresholds)).length;

  const draftOf = (id: string): IDraft => drafts[id] ?? { decision: '', reason: '' };
  const setDraft = (id: string, next: Partial<IDraft>) =>
    setDrafts((current) => ({ ...current, [id]: { ...draftOf(id), ...next } }));

  const record = (entry: IDemoPerson) => {
    const draft = draftOf(entry.id);
    if (!isReady(draft)) return;
    watch(entry.id);
    dispatch({
      type: 'eligibility/set',
      personIds: [entry.id],
      eligibility: draft.decision,
      reason: draft.decision === 'Not eligible' ? draft.reason : undefined,
      by: acting.name
    });
    setDrafts((current) => {
      const next = { ...current };
      delete next[entry.id];
      return next;
    });
  };

  const text = (row: IListRow, value: React.ReactNode) => (row.leaving ? <Struck>{value}</Struck> : value);

  const columns: IDataColumn<IListRow>[] = [
    { id: 'advisor', header: 'Advisor', cell: (row) => <AdvisorCell row={row} /> },
    { id: 'firm', header: 'Firm', cell: (row) => text(row, firmName(row.person.firmId)) },
    { id: 'state', header: 'State', cell: (row) => text(row, row.person.state) },
    { id: 'aum', header: 'AUM ($M)', numeric: true, cell: (row) => text(row, millions(row.person.aum)) },
    {
      id: 'production',
      header: 'Production ($M)',
      numeric: true,
      cell: (row) => text(row, millions(row.person.production))
    },
    { id: 'status', header: 'Survey status', cell: (row) => text(row, row.person.status2027) },
    {
      id: 'consideration',
      header: 'In consideration',
      cell: (row) => {
        if (row.leaving) return null;
        if (!open || !crosses(row.person, thresholds)) {
          return <span className="text-muted-foreground">Below the thresholds</span>;
        }
        return (
          <StatusChip tone="info" dot>
            In consideration
          </StatusChip>
        );
      }
    },
    {
      id: 'decision',
      header: 'Decision',
      cell: (row) => {
        if (row.leaving) return <Struck>{row.person.eligibility}</Struck>;
        const draft = draftOf(row.person.id);
        return (
          <div className="flex flex-wrap items-center gap-2">
            <DemoSelect
              aria-label={`Decision for ${row.person.name}`}
              value={draft.decision}
              disabled={!acts}
              onChange={(event) => setDraft(row.person.id, { decision: event.target.value as IDraft['decision'] })}
            >
              <option value="">Choose…</option>
              {DECISIONS.map((decision) => (
                <option key={decision} value={decision}>
                  {decision}
                </option>
              ))}
            </DemoSelect>
            {draft.decision === 'Not eligible' && (
              <DemoSelect
                aria-label={`Reason for ${row.person.name}`}
                value={draft.reason}
                disabled={!acts}
                onChange={(event) => setDraft(row.person.id, { reason: event.target.value })}
                className="max-w-56"
              >
                <option value="">Choose a reason…</option>
                {reasons.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.value}
                  </option>
                ))}
              </DemoSelect>
            )}
            <Button
              type="button"
              variant="outline"
              className="h-7 px-2.5"
              disabled={!acts || !isReady(draft)}
              onClick={() => record(row.person)}
            >
              Record the decision for {row.person.name}
            </Button>
          </div>
        );
      }
    }
  ];

  return (
    <MockPanel
      title={`${SURVEY_TYPE_LABEL[survey]} — not yet reviewed`}
      description={
        open
          ? `The 2027 survey is open until ${day(SURVEY_CLOSES)}: a record crossing the ` +
            'thresholds is flagged in consideration.'
          : 'The 2027 survey has closed: nothing is flagged in consideration any more.'
      }
      actions={<SurveyPicker survey={survey} onChange={setSurvey} />}
    >
      <div className="grid gap-2 text-[0.8125rem]">
        <p role="status" className="min-h-5 text-[var(--color-royal-blue)]">
          {said}
        </p>
        <DataTable
          label={`${SURVEY_TYPE_LABEL[survey]} initial review`}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.person.id}
          rowProps={leavingRowProps}
          minWidth="64rem"
          maxHeight="none"
          toolbar={
            <span className="text-muted-foreground tabular-nums">
              {members.length} not yet reviewed{open ? ` · ${flagged} in consideration` : ''}
            </span>
          }
          state={{
            kind: 'empty',
            content: `Every ${SURVEY_TYPE_LABEL[survey]} record has been reviewed.`
          }}
        />
        {!acts && (
          <p className="text-muted-foreground">The decisions are read-only for the role viewed as.</p>
        )}
      </div>
    </MockPanel>
  );
}
